import type { TFunction } from 'i18next';

import {
    type KpiExclusionReason,
    type KpiMetric,
    leftOutPens,
    penLabel,
} from '@/domain/feedingDashboard';

import { exclusionReasonText } from '../exclusionReasonText';
import { systemLabels } from '../systemLabel';

import type { KpiCardProps } from './kpiCardFormat';

export interface CoverageEntry {
    metric: KpiMetric;
    /** The value's label on the card; omitted on a card with a single value. */
    label?: string;
    /**
     * The label names the planning system ("Mercatus", "Mercatus actual"), so a reason
     * printed after it can drop the name. Not for a label such as "Bioscope" on a value
     * that needs a planning-system signal: "Bioscope: Signal not mapped" would blame the
     * Bioscope.
     */
    namesSystem?: boolean;
}

export interface KpiNote {
    /** "3 of 5 pens", one line per value the selection only partly covers. */
    note?: string[];
    /** One line per pen left out, with the reason. */
    noteReasons?: string[];
}

const PERIOD_METRICS: ReadonlySet<KpiMetric> = new Set(['feedActual', 'feedPlan', 'fallthrough']);

/**
 * Which pens a card's values cover, when fewer than all selected (IPS 5, 2.2.1). A mean
 * over some of the pens is still the right figure, but read as "the selection" it would
 * overstate what it rests on, so the card says so and names the pens left out.
 */
export function coverageNote(entries: CoverageEntry[], props: KpiCardProps, t: TFunction): KpiNote {
    const { summary, pens, penNames, config, closingDay } = props;
    const total = summary.coverage.selectedPens;
    const system = systemLabels(t, config.pmsSystemName).name;
    const notes: string[] = [];
    const lines: string[] = [];

    for (const entry of entries) {
        const covered = summary.coverage[entry.metric].length;
        if (covered >= total) continue;
        const left = leftOutPens(pens, summary.coverage, entry.metric);
        // `labelled`: whether the entry's label is printed before this reason.
        const reasonText = (reason: KpiExclusionReason, labelled: boolean): string =>
            exclusionReasonText(reason, t, {
                system,
                closingDay,
                period: PERIOD_METRICS.has(entry.metric),
                systemNamed: labelled && entry.namesSystem === true,
            });
        // With a single pen selected, "0 of 1 pen" says less than the reason itself.
        const count =
            total === 1 && left.length === 1
                ? reasonText(left[0].reason, Boolean(entry.label))
                : t('feedingModule.kpi.penCoverage', { covered, count: total });
        notes.push(entry.label ? `${entry.label}: ${count}` : count);
        if (total > 1) {
            // A single-value card leaves its label off the tooltip lines.
            const prefixed = entries.length > 1 && Boolean(entry.label);
            const prefix = prefixed ? `${entry.label}, ` : '';
            lines.push(
                ...left.map(
                    ({ pen, reason }) =>
                        `${prefix}${penLabel(penNames, pen)}: ${reasonText(reason, prefixed)}`
                )
            );
        }
    }

    return {
        note: notes.length > 0 ? notes : undefined,
        noteReasons: lines.length > 0 ? lines : undefined,
    };
}
